import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Chart } from 'primereact/chart';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { Skeleton } from 'primereact/skeleton';
import { Avatar } from 'primereact/avatar';
import { Tag } from 'primereact/tag';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Paginator } from 'primereact/paginator';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import storageUsageService from '../../services/storageUsageService';

// Chart.js needs concrete colors at render — PrimeReact CSS vars can't be read
// synchronously across all themes. Scoped to chart data only; app UI colors
// come from var(--primary-color) / severity props per v2.0.
const CHART_COLORS = ['#3B82F6', '#22C55E', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899', '#6366F1', '#14B8A6', '#F97316'];

const SECTION_LABEL = {
    leads: 'Leads',
    reports: 'Reports',
    profile: 'Profile Documents',
    bank_statements: 'Bank Statements',
    profiles: 'Profile Photos',
    feed: 'Social Feed',
    marketplace: 'Marketplace',
    conversions: 'Conversions',
    messaging: 'Messaging',
    establishment: 'Establishment',
};

const SECTION_ICON = {
    leads: 'pi-users',
    reports: 'pi-file',
    profile: 'pi-id-card',
    bank_statements: 'pi-wallet',
    profiles: 'pi-image',
    feed: 'pi-comments',
    marketplace: 'pi-shopping-cart',
    conversions: 'pi-sync',
    messaging: 'pi-envelope',
    establishment: 'pi-building',
};

const getSectionLabel = (s) => SECTION_LABEL[s] || s;
const getSectionIcon = (s) => SECTION_ICON[s] || 'pi-folder';

// KPI card (P15)
const KpiCard = ({ icon, label, value, description, loading }) => (
    <div className="col-6 lg:col-3">
        <Card className="h-full shadow-1" pt={{ body: { className: 'p-3' } }}>
            <div className="flex justify-content-between align-items-start">
                <div className="flex flex-column gap-1">
                    <span className="text-600 text-sm font-medium">{label}</span>
                    <span className="text-900 text-xl font-semibold">
                        {loading ? <Skeleton width="4rem" height="1.5rem" /> : value}
                    </span>
                </div>
                <div className="flex align-items-center justify-content-center border-round bg-primary-50 p-2">
                    <i className={`pi ${icon} text-xl text-primary`} aria-hidden="true"></i>
                </div>
            </div>
            <span className="text-500 text-xs block mt-2">{description}</span>
        </Card>
    </div>
);

// Mobile AccordionTab header — user primary info + storage tag + view button
const UserHeader = ({ user, onView }) => (
    <div className="flex justify-content-between align-items-center gap-2 w-full">
        <div className="flex align-items-center gap-2 flex-1 min-w-0">
            <Avatar
                label={user.name?.charAt(0)?.toUpperCase() || '?'}
                shape="circle"
                className="flex-shrink-0"
            />
            <div className="flex flex-column min-w-0">
                <span
                    className="font-medium text-sm text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis"
                    title={user.name}
                >
                    {user.name || '—'}
                </span>
                <span
                    className="text-xs text-500 white-space-nowrap overflow-hidden text-overflow-ellipsis"
                    title={user.email}
                >
                    {user.email || '—'}
                </span>
            </div>
        </div>
        <div className="flex align-items-center gap-1 flex-shrink-0">
            <Tag
                value={user.total_formatted || '0 B'}
                severity={user.total_bytes > 0 ? 'info' : 'secondary'}
                className="text-xs"
            />
            <div onClick={(e) => e.stopPropagation()}>
                <Button
                    icon="pi pi-eye"
                    rounded
                    text
                    size="small"
                    aria-label="View storage details"
                    onClick={() => onView(user)}
                />
            </div>
        </div>
    </div>
);

// Mobile AccordionTab content — mirrors all desktop columns
const UserDetails = ({ user }) => (
    <div className="flex flex-column gap-2 text-xs">
        <div className="flex align-items-center gap-2">
            <i className="pi pi-hashtag text-500 w-1rem" aria-hidden="true" />
            <span className="text-500 font-medium w-6rem">ID</span>
            <span className="text-700">#{user.id}</span>
        </div>
        <div className="flex align-items-center gap-2">
            <i className="pi pi-building text-500 w-1rem" aria-hidden="true" />
            <span className="text-500 font-medium w-6rem">Company</span>
            <span className={user.company?.name ? 'text-700' : 'text-500'}>
                {user.company?.name || '—'}
            </span>
        </div>
        <div className="flex align-items-center gap-2">
            <i className="pi pi-file text-500 w-1rem" aria-hidden="true" />
            <span className="text-500 font-medium w-6rem">Files</span>
            <span className="text-700">{user.total_files?.toLocaleString() || '0'}</span>
        </div>
        <div className="flex align-items-center gap-2">
            <i className="pi pi-clock text-500 w-1rem" aria-hidden="true" />
            <span className="text-500 font-medium w-6rem">Last Upload</span>
            <span className={user.last_upload_at ? 'text-700' : 'text-500'}>
                {user.last_upload_at ? storageUsageService.formatDate(user.last_upload_at) : '—'}
            </span>
        </div>
    </div>
);

const TenantStorageUsage = () => {
    const toast = useRef(null);

    // Access control
    const userRoles = JSON.parse(localStorage.getItem('userRoles') || '[]');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const primaryRole = userRoles.length > 0 ? userRoles[0] : (user.user_type || 'user');
    const isTenantAdmin = ['tenant_admin', 'owner'].includes(primaryRole);

    // Data
    const [data, setData] = useState(null);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination + sort
    const [totalRecords, setTotalRecords] = useState(0);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);
    const [sortField, setSortField] = useState('name');
    const [sortOrder, setSortOrder] = useState(1);

    // Filters
    const [companies, setCompanies] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [loadingCompanies, setLoadingCompanies] = useState(false);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // Mobile accordion state (P17)
    const [mobileActiveIndex, setMobileActiveIndex] = useState(null);

    // Detail dialog
    const [detailVisible, setDetailVisible] = useState(false);
    const [detailUser, setDetailUser] = useState(null);
    const [detailData, setDetailData] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    // Refresh nonce — incrementing this forces the load effect to re-fire
    // even when pagination / filter state didn't change
    const [refreshNonce, setRefreshNonce] = useState(0);

    // Debounced search (P13) — 500ms, reset pagination
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search.trim());
            setFirst(0);
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    // Load companies once
    useEffect(() => {
        if (!isTenantAdmin) return;
        const fetchCompanies = async () => {
            setLoadingCompanies(true);
            try {
                const response = await storageUsageService.getCompaniesForFilter();
                if (response.success) setCompanies(response.items || []);
            } catch (err) {
                console.error('Failed to load companies:', err?.message || 'Unknown error');
            } finally {
                setLoadingCompanies(false);
            }
        };
        fetchCompanies();
    }, [isTenantAdmin]);

    // Load usage whenever primitive filter/page/sort deps change (P14)
    useEffect(() => {
        if (!isTenantAdmin) return;
        let cancelled = false;
        const loadData = async () => {
            setLoading(true);
            setError(null);
            try {
                const page = Math.floor(first / rows) + 1;
                const params = {
                    page,
                    limit: rows,
                    sort_field: sortField,
                    sort_order: sortOrder === 1 ? 'asc' : 'desc',
                };
                if (selectedCompany) params.company_ID = selectedCompany;
                if (debouncedSearch) params.search = debouncedSearch;

                const response = await storageUsageService.getTenantUsage(params);
                if (cancelled) return;
                if (response.data) {
                    setData(response.data);
                    setUsers(response.data.users || []);
                    setTotalRecords(response.data.pagination?.total || 0);
                } else {
                    setUsers([]);
                    setTotalRecords(0);
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('Failed to load storage data:', err?.message || 'Unknown error');
                    setError('Failed to load storage data.');
                    setUsers([]);
                    setTotalRecords(0);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        loadData();
        return () => { cancelled = true; };
    }, [isTenantAdmin, first, rows, sortField, sortOrder, selectedCompany, debouncedSearch, refreshNonce]);

    // Reset mobile expanded row when result set changes (P17)
    useEffect(() => {
        setMobileActiveIndex(null);
    }, [first, selectedCompany, debouncedSearch]);

    const clearFilters = () => {
        setSearch('');
        setSelectedCompany(null);
        setFirst(0);
    };

    const refresh = () => {
        setRefreshNonce((n) => n + 1);
    };

    const openUserDetail = useCallback(async (rowData) => {
        setDetailUser(rowData);
        setDetailData(null);
        setDetailVisible(true);
        setDetailLoading(true);
        try {
            const response = await storageUsageService.getUserUsage(rowData.id);
            if (response.success) setDetailData(response.data);
        } catch (err) {
            console.error('Failed to load user usage:', err?.message || 'Unknown error');
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load user storage details' });
        } finally {
            setDetailLoading(false);
        }
    }, []);

    // Chart data for dashboard bar chart — per-bar colors keep the visual
    // link with the section breakdown list in the detail dialog
    const chartData = useMemo(() => {
        const sections = data?.summary?.by_section;
        if (!sections || sections.length === 0) return null;
        return {
            labels: sections.map((s) => getSectionLabel(s.section)),
            datasets: [{
                data: sections.map((s) => s.bytes),
                backgroundColor: CHART_COLORS.slice(0, sections.length),
                borderWidth: 0,
                hoverOffset: 6,
            }],
        };
    }, [data]);

    const detailChartData = useMemo(() => {
        const sections = detailData?.by_section;
        if (!sections || sections.length === 0) return null;
        return {
            labels: sections.map((s) => getSectionLabel(s.section)),
            datasets: [{
                data: sections.map((s) => s.bytes),
                backgroundColor: CHART_COLORS.slice(0, sections.length),
                borderWidth: 0,
                hoverOffset: 6,
            }],
        };
    }, [detailData]);

    // Doughnut options — compact, no built-in legend (we render an adjacent
    // breakdown list with exact values). Tooltip shows bytes + percent.
    const chartOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: true,
        cutout: '65%',
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (ctx) => {
                        const value = storageUsageService.formatBytes(ctx.raw);
                        const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                        const pct = total > 0 ? ((ctx.raw / total) * 100).toFixed(1) : '0.0';
                        return ` ${value} (${pct}%)`;
                    },
                },
            },
        },
    }), []);

    const sections = data?.summary?.by_section || [];

    const detailSections = detailData?.by_section || [];
    const detailTotalBytes = detailData?.total_bytes || 0;

    const avgPerCompany = useMemo(() => {
        const tb = data?.summary?.total_bytes || 0;
        const cc = data?.summary?.unique_companies || 1;
        return storageUsageService.formatBytes(Math.round(tb / cc));
    }, [data]);

    // Access denied — after hooks, before render
    if (!isTenantAdmin) {
        return (
            <>
                <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-3 mb-4">
                    <div>
                        <h1 className="text-xl font-semibold text-900 m-0 mb-1">Storage Usage</h1>
                        <p className="text-600 text-sm m-0">Monitor storage across all companies and users</p>
                    </div>
                </div>
                <Card>
                    <Message severity="error" text="You need admin privileges to access this page." className="w-full" />
                </Card>
            </>
        );
    }

    // DataTable column templates
    const idBodyTemplate = (rowData) => <span className="text-sm text-500">#{rowData.id}</span>;

    const nameBodyTemplate = (rowData) => (
        <div className="flex align-items-center gap-2">
            <Avatar
                label={rowData.name?.charAt(0)?.toUpperCase() || '?'}
                shape="circle"
                size="normal"
                className="flex-shrink-0"
            />
            <div className="flex flex-column">
                <span className="font-medium text-sm text-900">{rowData.name}</span>
                <span className="text-xs text-500">{rowData.email}</span>
                {rowData.company && (
                    <span className="text-xs text-500 mt-1">
                        <i className="pi pi-building mr-1" aria-hidden="true" />
                        {rowData.company.name}
                    </span>
                )}
            </div>
        </div>
    );

    const storageBodyTemplate = (rowData) => (
        <span className="font-semibold text-sm text-primary">{rowData.total_formatted || '0 B'}</span>
    );

    const filesBodyTemplate = (rowData) => (
        <span className="text-sm text-700">{rowData.total_files?.toLocaleString() || '0'}</span>
    );

    const lastUploadBodyTemplate = (rowData) => (
        <span className={`text-sm ${rowData.last_upload_at ? 'text-700' : 'text-500'}`}>
            {rowData.last_upload_at ? storageUsageService.formatDate(rowData.last_upload_at) : '—'}
        </span>
    );

    const actionBodyTemplate = (rowData) => (
        <Button
            icon="pi pi-eye"
            severity="info"
            outlined
            size="small"
            aria-label="View storage details"
            tooltip="View details"
            tooltipOptions={{ position: 'left' }}
            onClick={() => openUserDetail(rowData)}
        />
    );

    return (
        <>
            <Toast ref={toast} />

            {/* Page header (P6) */}
            <div className="mb-4">
                <h1 className="text-xl font-semibold text-900 m-0 mb-1">Storage Usage</h1>
                <p className="text-600 text-sm m-0">Monitor storage across all companies and users</p>
            </div>

            {error && <Message severity="error" text={error} className="w-full mb-3" />}

            {/* KPI cards (P15) */}
            <div className="grid mb-3">
                <KpiCard
                    icon="pi-database"
                    label="Total Storage"
                    value={data?.summary?.total_formatted || '0 B'}
                    description="Across all companies"
                    loading={loading}
                />
                <KpiCard
                    icon="pi-building"
                    label="Companies"
                    value={data?.summary?.unique_companies || 0}
                    description="Active companies with data"
                    loading={loading}
                />
                <KpiCard
                    icon="pi-users"
                    label="Total Users"
                    value={data?.summary?.unique_users || totalRecords || 0}
                    description="Users with storage footprint"
                    loading={loading}
                />
                <KpiCard
                    icon="pi-chart-bar"
                    label="Avg / Company"
                    value={avgPerCompany}
                    description="Average storage per company"
                    loading={loading}
                />
            </div>

            {/* Storage Distribution — small doughnut + breakdown list (Stripe / GitHub pattern) */}
            {!loading && chartData && sections.length > 0 && (
                <Card className="mb-3">
                    <div className="flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                        <h2 className="text-base font-medium text-900 m-0">Storage Distribution</h2>
                        {selectedCompany && (
                            <span className="text-xs text-500">
                                <i className="pi pi-filter mr-1" aria-hidden="true" />
                                {companies.find((c) => c.id === selectedCompany)?.name || 'Filtered'}
                            </span>
                        )}
                    </div>
                    <div className="flex flex-column md:flex-row align-items-center md:align-items-start gap-4">
                        {/* Compact doughnut on the left */}
                        <div className="flex-shrink-0" style={{ width: '180px', height: '180px' }}>
                            <Chart
                                type="doughnut"
                                data={chartData}
                                options={chartOptions}
                                className="w-full h-full"
                            />
                        </div>
                        {/* Breakdown list on the right — color dot + label + value + percent */}
                        <div className="flex-grow-1 w-full">
                            <div className="flex flex-column gap-2">
                                {sections.map((s, i) => {
                                    const total = data?.summary?.total_bytes || 0;
                                    const pct = total > 0 ? ((s.bytes / total) * 100).toFixed(1) : '0.0';
                                    return (
                                        <div
                                            key={s.section}
                                            className="flex align-items-center justify-content-between gap-2 border-bottom-1 surface-border pb-2"
                                        >
                                            <div className="flex align-items-center gap-2 min-w-0">
                                                <span
                                                    className="border-circle flex-shrink-0"
                                                    style={{
                                                        width: '10px',
                                                        height: '10px',
                                                        backgroundColor: CHART_COLORS[i % CHART_COLORS.length],
                                                    }}
                                                    aria-hidden="true"
                                                />
                                                <span className="text-sm text-700 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                                    {getSectionLabel(s.section)}
                                                </span>
                                            </div>
                                            <div className="flex align-items-center gap-2 flex-shrink-0">
                                                <span className="text-sm font-semibold text-900">{s.formatted}</span>
                                                <span className="text-xs text-500 w-3rem text-right">{pct}%</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </Card>
            )}

            {/* Users list — single Card wraps filter bar + data list (P20) */}
            <Card>
                {/* Filter bar (P7 — 2 filter pattern) — INSIDE same Card as data list */}
                <div className="surface-ground p-3 border-round mb-3">
                    <div className="grid">
                        <div className="col-6 md:col">
                            <Dropdown
                                value={selectedCompany}
                                options={companies}
                                onChange={(e) => { setSelectedCompany(e.value); setFirst(0); }}
                                optionLabel="name"
                                optionValue="id"
                                filter
                                filterBy="name"
                                placeholder="All Companies"
                                showClear
                                className="w-full p-inputtext-sm"
                                loading={loadingCompanies}
                                emptyMessage="No companies found"
                            />
                        </div>
                        <div className="col-6 md:col">
                            <IconField iconPosition="left" className="w-full">
                                <InputIcon className="pi pi-search" />
                                <InputText
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search by name, email or mobile"
                                    maxLength={100}
                                    className="w-full p-inputtext-sm"
                                />
                            </IconField>
                        </div>
                        <div className="hidden md:block md:col" aria-hidden="true"></div>
                        <div className="hidden md:block md:col" aria-hidden="true"></div>
                        <div className="col-12 md:col flex gap-2">
                            <Button
                                label="Clear"
                                icon="pi pi-filter-slash"
                                severity="secondary"
                                outlined
                                size="small"
                                className="flex-1 py-1 md:py-2"
                                onClick={clearFilters}
                                disabled={!search && !selectedCompany}
                            />
                            <Button
                                label="Refresh"
                                icon="pi pi-refresh"
                                size="small"
                                className="flex-1 py-1 md:py-2"
                                onClick={refresh}
                                loading={loading}
                            />
                        </div>
                    </div>
                </div>

                {/* Mobile (< md): Accordion cards */}
                <div className="block md:hidden">
                    {loading && (
                        <div className="flex flex-column gap-2">
                            <Skeleton height="70px" />
                            <Skeleton height="70px" />
                            <Skeleton height="70px" />
                        </div>
                    )}
                    {!loading && users.length === 0 && (
                        <div className="flex flex-column align-items-center py-5">
                            <i className="pi pi-inbox pi-2x text-400 mb-3" aria-hidden="true" />
                            <span className="text-600">No storage usage data found</span>
                        </div>
                    )}
                    {!loading && users.length > 0 && (
                        <>
                            <Accordion
                                activeIndex={mobileActiveIndex}
                                onTabChange={(e) => setMobileActiveIndex(e.index)}
                            >
                                {users.map((u) => (
                                    <AccordionTab
                                        key={u.id}
                                        header={<UserHeader user={u} onView={openUserDetail} />}
                                    >
                                        <UserDetails user={u} />
                                    </AccordionTab>
                                ))}
                            </Accordion>
                            <Paginator
                                first={first}
                                rows={rows}
                                totalRecords={totalRecords}
                                onPageChange={(e) => { setFirst(e.first); setRows(e.rows); }}
                                template="PrevPageLink PageLinks NextPageLink CurrentPageReport"
                                currentPageReportTemplate="{first}-{last} of {totalRecords}"
                                pageLinkSize={3}
                                className="text-sm mt-2"
                            />
                        </>
                    )}
                </div>

                {/* Desktop (≥ md): DataTable with frozen User + Actions */}
                <div className="hidden md:block">
                    <DataTable
                        value={users}
                        dataKey="id"
                        lazy
                        loading={loading}
                        paginator
                        first={first}
                        rows={rows}
                        totalRecords={totalRecords}
                        onPage={(e) => { setFirst(e.first); setRows(e.rows); }}
                        sortField={sortField}
                        sortOrder={sortOrder}
                        onSort={(e) => { setSortField(e.sortField); setSortOrder(e.sortOrder); setFirst(0); }}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                        currentPageReportTemplate="Showing {first}-{last} of {totalRecords}"
                        paginatorClassName="text-sm"
                        pageLinkSize={3}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        responsiveLayout="scroll"
                        scrollable
                        stripedRows
                        showGridlines
                        size="small"
                        className="p-datatable-gridlines"
                        emptyMessage={
                            <div className="flex flex-column align-items-center py-5">
                                <i className="pi pi-inbox pi-2x text-400 mb-3" aria-hidden="true" />
                                <span className="text-600">No storage usage data found</span>
                            </div>
                        }
                    >
                        <Column field="id" header="ID" body={idBodyTemplate} style={{ width: '80px' }} />
                        <Column
                            field="name"
                            header="User & Company"
                            body={nameBodyTemplate}
                            sortable
                            frozen
                            alignFrozen="left"
                            style={{ minWidth: '260px' }}
                        />
                        <Column
                            field="file_count"
                            header="Files"
                            body={filesBodyTemplate}
                            sortable
                            style={{ minWidth: '100px' }}
                        />
                        <Column
                            field="total_bytes"
                            header="Storage"
                            body={storageBodyTemplate}
                            sortable
                            style={{ minWidth: '120px' }}
                        />
                        <Column
                            field="last_upload_at"
                            header="Last Upload"
                            body={lastUploadBodyTemplate}
                            sortable
                            style={{ minWidth: '160px' }}
                        />
                        <Column
                            header="Actions"
                            body={actionBodyTemplate}
                            frozen
                            alignFrozen="right"
                            style={{ minWidth: '80px', width: '80px' }}
                        />
                    </DataTable>
                </div>
            </Card>

            {/* Detail dialog (P4) */}
            <Dialog
                visible={detailVisible}
                onHide={() => setDetailVisible(false)}
                modal
                draggable={false}
                dismissableMask
                style={{ width: '90vw', maxWidth: '600px' }}
                header={
                    detailUser ? (
                        <div className="flex align-items-center gap-3">
                            <Avatar
                                label={detailUser.name?.charAt(0)?.toUpperCase() || '?'}
                                shape="circle"
                                size="large"
                            />
                            <div className="flex flex-column">
                                <span className="font-semibold text-900 text-base">{detailUser.name || '—'}</span>
                                <span className="text-500 text-xs">{detailUser.email || '—'}</span>
                                {detailUser.company && (
                                    <span className="text-500 text-xs">
                                        <i className="pi pi-building mr-1" aria-hidden="true" />
                                        {detailUser.company.name}
                                    </span>
                                )}
                            </div>
                        </div>
                    ) : 'User Storage'
                }
            >
                {/* Summary strip */}
                <div className="grid mb-3">
                    <div className="col-4 text-center">
                        <div className="text-xs text-500 mb-1">Total Storage</div>
                        <div className="font-semibold text-sm text-primary">
                            {detailLoading ? <Skeleton width="3rem" height="1rem" className="mx-auto" /> : (detailData?.total_formatted || '0 B')}
                        </div>
                    </div>
                    <div className="col-4 text-center">
                        <div className="text-xs text-500 mb-1">Total Files</div>
                        <div className="font-semibold text-sm text-900">
                            {detailLoading ? <Skeleton width="2rem" height="1rem" className="mx-auto" /> : (detailData?.total_files?.toLocaleString() || '0')}
                        </div>
                    </div>
                    <div className="col-4 text-center">
                        <div className="text-xs text-500 mb-1">Sections</div>
                        <div className="font-semibold text-sm text-900">
                            {detailLoading ? <Skeleton width="1.5rem" height="1rem" className="mx-auto" /> : detailSections.length}
                        </div>
                    </div>
                </div>

                {detailLoading && (
                    <div className="flex flex-column gap-2">
                        <Skeleton width="100%" height="140px" />
                        <Skeleton width="100%" height="1.5rem" />
                        <Skeleton width="100%" height="1.5rem" />
                        <Skeleton width="100%" height="1.5rem" />
                    </div>
                )}

                {!detailLoading && detailSections.length === 0 && (
                    <div className="flex flex-column align-items-center py-5">
                        <i className="pi pi-inbox pi-2x text-400 mb-3" aria-hidden="true" />
                        <span className="text-600">No storage data available for this user</span>
                    </div>
                )}

                {!detailLoading && detailSections.length > 0 && (
                    <div className="flex flex-column md:flex-row align-items-center md:align-items-start gap-4">
                        {/* Compact doughnut on the left */}
                        <div className="flex-shrink-0" style={{ width: '160px', height: '160px' }}>
                            <Chart
                                type="doughnut"
                                data={detailChartData}
                                options={chartOptions}
                                className="w-full h-full"
                            />
                        </div>
                        {/* Section breakdown list on the right — dot + icon + label + value + % + files */}
                        <div className="flex-grow-1 w-full overflow-y-auto" style={{ maxHeight: '280px' }}>
                            <div className="flex flex-column gap-2">
                                {detailSections.map((section, i) => {
                                    const pct = detailTotalBytes > 0 ? ((section.bytes / detailTotalBytes) * 100).toFixed(1) : '0.0';
                                    const color = CHART_COLORS[i % CHART_COLORS.length];
                                    return (
                                        <div
                                            key={section.section}
                                            className="flex align-items-center justify-content-between gap-2 border-bottom-1 surface-border pb-2"
                                        >
                                            <div className="flex align-items-center gap-2 min-w-0">
                                                <span
                                                    className="border-circle flex-shrink-0"
                                                    style={{ width: '10px', height: '10px', backgroundColor: color }}
                                                    aria-hidden="true"
                                                />
                                                <i className={`pi ${getSectionIcon(section.section)} text-500 text-xs flex-shrink-0`} aria-hidden="true" />
                                                <div className="flex flex-column min-w-0">
                                                    <span className="text-sm text-700 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                                        {getSectionLabel(section.section)}
                                                    </span>
                                                    <span className="text-xs text-500">{section.files} files</span>
                                                </div>
                                            </div>
                                            <div className="flex align-items-center gap-2 flex-shrink-0">
                                                <span className="text-sm font-semibold text-900">{section.formatted}</span>
                                                <span className="text-xs text-500 w-3rem text-right">{pct}%</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </Dialog>
        </>
    );
};

export default TenantStorageUsage;
